import { v4 as uuid } from "uuid";

import { _ } from "js/vendor";
import getLogger, { LogGroup } from "js/core/logger";
import { PusherEventType, SLIDE_COMMAND_BLACKLIST } from "common/constants";
import Api from "js/core/api";
import pusher from "js/core/services/pusher";
import applyCommands from "common/commands/applyCommands";
import deriveSimpleModel from "common/commands/deriveSimpleModel";
import removeTrailingNullsFromArrays from "common/utils/removeTrailingNullsFromArrays";

import CommandsAdapter from "./commandsAdapter";
import Adapter from "./adapter";

const logger = getLogger(LogGroup.ADAPTER);

class SlidesAdapter extends Adapter {
    constructor({
        model,
        autoSync = true,
        readonly = false,
        // Only used for passing to commands adapters
        userId = null
    }) {
        super();

        this.model = model;
        this.autoSync = autoSync;
        this.readonly = readonly;
        this.userId = userId;

        this.fallbackToCommandsAdapter = false;
        this.commandsAdapter = null;

        this.connected = false;

        this._asyncActionsPromiseChain = Promise.resolve();

        this.pusherEventChunksBuffer = {};
    }

    connect({ id = null, onRemoteChange = () => { }, onRemoteError = () => { }, data = null }) {
        if (data && id) {
            throw new Error("Initializing slide with both id and initial data is not permitted");
        }

        if (!data && !id) {
            throw new Error("Initial data or id must be present");
        }

        if (this.readonly && data) {
            throw new Error("Cannot create new record in readonly mode");
        }

        // Auto generating an id if not passed
        this.id = _.defaultTo(id, uuid());

        // Wrapping callbacks to allow logging
        this.onRemoteChange = (changeType, data) => {
            // We have to remove trailing nulls from arrays since there's no handy way to entirely remove elements from arrays
            // in Mongo so "removed" elements are marked as nulls instead, so we have to trim them
            const normalizedData = removeTrailingNullsFromArrays(_.cloneDeep(data));
            return onRemoteChange(changeType, normalizedData);
        };
        this.onRemoteError = err => {
            logger.error(err, "[SlidesAdapter] onRemoteError()", { id: this.id });
            return onRemoteError(err);
        };

        // Initializing (loading/setting the data)
        this._initialize(data);

        this.connected = true;

        // Returning the model id
        return this.id;
    }

    disconnect() {
        if (!this.connected) {
            return;
        }

        if (this.pusherChannel) {
            this.pusherChannel.unbind(PusherEventType.DATA_RECORD_UPDATED, this._onPusherEvent);
            // Will unsubscribe if only nobody else is using the channel (e.g. another slide model instance)
            if (!this.pusherChannel.isInUse) {
                pusher.unsubscribe(this.pusherChannel.name);
            }
            this.pusherChannel = null;
        }

        if (this.fallbackToCommandsAdapter) {
            this.commandsAdapter.disconnect();
        }

        this.connected = false;

        // NOTE: there may be still background updates running after the
        // adapter has been disconnected
    }

    update(type, changesetUpdates, changesetOriginal) {
        if (this.fallbackToCommandsAdapter) {
            return this.commandsAdapter.update(type, changesetUpdates, changesetOriginal);
        }

        if (!this.isConnected()) {
            throw new Error("Adapter is not connected");
        }

        if (this.readonly) {
            throw new Error("Adapter is in read only mode, cannot update");
        }

        switch (type) {
            case Adapter.TYPE.remove:
                throw new Error("Remove update type not supported");
            case Adapter.TYPE.replace:
                throw new Error("Replace update type not supported");
            case Adapter.TYPE.update:
                return this._handleUpdate(changesetUpdates, changesetOriginal);
            default:
                throw new Error(`Unsupported update type ${type}`);
        }
    }

    isConnected() {
        if (this.fallbackToCommandsAdapter) {
            return this.commandsAdapter.isConnected();
        }

        return this.connected;
    }

    createUid() {
        if (this.fallbackToCommandsAdapter) {
            return this.commandsAdapter.createUid();
        }

        return uuid();
    }

    _onPusherEvent = payload => {
        this._asyncActionsPromiseChain = this._asyncActionsPromiseChain
            .then(() => {
                // May have been disconnected while was waiting for the changes
                // to be written
                if (!this.connected) {
                    return;
                }

                const decodeAndParseChunksData = encodedData =>
                    // Note: decodeURIComponent and escape are needed in order to correctly handle extended unicode chars (2+ bytes)
                    JSON.parse(decodeURIComponent(escape(atob(encodedData))));

                const handleEvent = (original, update, triggeredAt) => {
                    if (triggeredAt <= this.model.attributes.modifiedAt) {
                        return;
                    }

                    // Data is a changeset
                    this.onRemoteChange(Adapter.TYPE.update, update);
                };

                const {
                    eventId,
                    chunksTotalCount,
                    chunkIndex,
                    chunk
                } = payload;

                // Message has only one chunk -> process immediately
                if (chunksTotalCount === 1) {
                    const { original, update, triggeredAt } = decodeAndParseChunksData(chunk);
                    handleEvent(original, update, triggeredAt);
                    return;
                }

                // Creating chunks buffer for event if not exists
                if (!this.pusherEventChunksBuffer[eventId]) {
                    this.pusherEventChunksBuffer[eventId] = new Array(chunksTotalCount);
                }

                // Saving received chunk under its index
                this.pusherEventChunksBuffer[eventId][chunkIndex] = chunk;

                // Checking if we have all chunks and composing the event if we do
                if (this.pusherEventChunksBuffer[eventId].filter(chunk => chunk).length === chunksTotalCount) {
                    const { original, update, triggeredAt } = decodeAndParseChunksData(this.pusherEventChunksBuffer[eventId].join(""));
                    delete this.pusherEventChunksBuffer[eventId];
                    handleEvent(original, update, triggeredAt);
                    return;
                }
            })
            .catch(err => {
                // Won't throw here to avoid breaking the promise chain
                logger.error(err, `[SlidesAdapter] _onPusherEvent() failed`, { id: this.id });

                this.onRemoteError(err);
            });
    }

    _handleUpdate(changesetUpdates, changesetOriginal) {
        const handleUpdate = async () => {
            try {
                const { modifiedAt, _changeId } = await Api.slides.put({ id: this.id, changesetUpdates, changesetOriginal, _changeId: this.model.attributes._changeId });
                // Report back the updated model values
                this.onRemoteChange(Adapter.TYPE.update, { modifiedAt, _changeId });
            } catch (err) {
                if (err.status === 409) {
                    logger.warn(`[SlidesAdapter] _handleUpdate() the model is out of sync with the server, will pull the latest version`, { id: this.id });
                    try {
                        const { slide } = await this._getSlideFromApi();
                        // Setting the server state
                        this.onRemoteChange(Adapter.TYPE.replace, slide);
                    } catch (err) {
                        logger.error(err, `[SlidesAdapter] _handleUpdate() _getSlideFromApi() failed`, { id: this.id });
                    }
                } else {
                    logger.error(err, `[SlidesAdapter] _handleUpdate() failed`, { id: this.id });
                    // Restoring the original state
                    this.onRemoteChange(Adapter.TYPE.update, changesetOriginal);
                }

                throw err;
            }
        };

        return new Promise((resolve, reject) => {
            this._asyncActionsPromiseChain = this._asyncActionsPromiseChain
                .then(handleUpdate)
                .then(resolve)
                .catch(reject);
        });
    }

    _getSlideFromApi() {
        return Api.slides.get({
            id: this.id,
            isReadOnlyMode: this.readonly,
            presentationId: (this.model.presentation && !this.model.presentation.get("isDummy")) ? this.model.presentation.id : null,
            presentationLinkId: this.model.presentation && this.model.presentation.get("link")?.id,
        });
    }

    _initialize(initialData) {
        this._asyncActionsPromiseChain = this._asyncActionsPromiseChain
            .then(async () => {
                if (initialData) {
                    // Creating new slide
                    let slideModel;
                    if (initialData.cmds) {
                        logger.warn(`[SlidesAdapter] _initialize() received initial data with commands, this should never happen, will migrate the model`, { id: this.id });
                        slideModel = this._composeSlideModel(initialData);
                    } else {
                        slideModel = { ...initialData, id: this.id };
                    }

                    // Force non-empty states
                    // It can be nulled by StorageModel on update because mergeChanges()
                    // removes empty objects and arrays to mimic Firebase behaviour
                    if (!slideModel.states) {
                        slideModel.states = [{}];
                    }

                    // Saving a new slide with the supplied data
                    slideModel = await Api.slides.post(slideModel);
                    // Reporting initialized
                    this.onRemoteChange(Adapter.TYPE.initialize, slideModel);
                    // Connecting pusher (if needed)
                    await this._connectPusher();
                    return;
                }

                // Getting the slide (will migrate it if needed/allowed)
                // The retry logic is necessary to handle cases when we're requesting a slide that's
                // being written to Mongo at the moment so the server may not know about its existence yet
                let slidesApiResponse = null;
                let apiFetchAttemptsLeft = 5;
                while (true) {
                    try {
                        slidesApiResponse = await this._getSlideFromApi();
                        break;
                    } catch (err) {
                        // 404 means slide not found so it's possible it's being written to Mongo at the moment
                        if (err.status === 404) {
                            if (apiFetchAttemptsLeft > 0) {
                                // Wait for 1s and retry
                                await new Promise(resolve => setTimeout(resolve, 1000));
                                apiFetchAttemptsLeft--;
                                continue;
                            }

                            throw new Error(`Failed to load slide ${this.id}, slide not found`);
                        }

                        // Migration failed, will try falling back to commands adapter
                        logger.error(err, `[SlidesAdapter] _initialize() Api.slides.get() request failed, falling back to commands adapter`, { id: this.id });
                        this._switchToCommandsAdapter();
                        return;
                    }
                }

                if (slidesApiResponse.migrated) {
                    // Reporting initialized
                    this.onRemoteChange(Adapter.TYPE.initialize, slidesApiResponse.slide);
                    // Connecting pusher (if needed)
                    await this._connectPusher();
                    return;
                }

                // Slide wasn't migrated, will use commands adapter
                this._switchToCommandsAdapter();
            })
            .catch(err => {
                logger.error(err, `[SlidesAdapter] [${this.id}] _initialize() failed`, { id: this.id });

                // NOTE: Since initialize() is called from a sync function
                // we should report errors by calling onRemoteError()
                this.onRemoteError(err);
            });
    }

    async _connectPusher() {
        if (!this.connected) {
            // NOTE: this may happen if disconnect() was called before initialization has finished
            return;
        }

        if (!this.autoSync) {
            return;
        }

        const pusherChannelId = `private-slide-${this.id}`;

        const pusherChannel = await pusher.subscribe(pusherChannelId);
        if (pusherChannel) {
            if (!this.connected) {
                // Will unsubscribe if only nobody else is using the channel (e.g. another slide model instance)
                if (!pusherChannel.isInUse) {
                    pusher.unsubscribe(pusherChannel.name);
                }
                return;
            }

            this.pusherChannel = pusherChannel;
            this.pusherChannel.bind(PusherEventType.DATA_RECORD_UPDATED, this._onPusherEvent);
        }
    }

    _composeSlideModel(firebaseSlide) {
        const commands = Object.entries(firebaseSlide.cmds)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(cmd => {
                cmd[1].id = cmd[0];
                return cmd[1];
            })
            .filter(command => command.id > firebaseSlide.snap.id);

        const updates = commands.reduce((list, cmd) => list.concat(JSON.parse(cmd.updates)), []);
        const commandsModel = applyCommands(JSON.parse(firebaseSlide.snap.model || "{}"), updates);

        // Merging commands fields and plain fields
        const slideModel = { ...firebaseSlide, ...deriveSimpleModel(commandsModel), id: this.id };
        delete slideModel.cmds;
        delete slideModel.snap;

        return slideModel;
    }

    _switchToCommandsAdapter() {
        this.fallbackToCommandsAdapter = true;
        this.commandsAdapter = new CommandsAdapter({
            writablePlainProperties: ["presentationId"],
            plainProperties: SLIDE_COMMAND_BLACKLIST,
            autoSync: this.autoSync,
            readonly: this.readonly,
            userId: this.userId
        });

        // This will trigger the "initialize" event upon connection
        this.commandsAdapter.connect({
            root: "slides",
            id: this.id,
            onRemoteChange: (changeType, data) => {
                // If the slide was migrated, then switch to Mongo
                if ([Adapter.TYPE.replace, Adapter.TYPE.update, Adapter.TYPE.initialize].includes(changeType) && data.migrationFinishedAt) {
                    logger.info("[SlidesAdapter] onRemoteChange() slide was migrated, switching to Mongo", { id: this.id });
                    this.commandsAdapter.disconnect();
                    this.commandsAdapter = null;
                    this.fallbackToCommandsAdapter = false;
                    // Reinitializing
                    this._initialize(null);
                    return;
                }

                return this.onRemoteChange(changeType, data);
            },
            onRemoteError: this.onRemoteError
        });
    }
}

export default SlidesAdapter;
